import api from '@mint-lib/api';
import { useMutation, useQueryClient } from '@mint-lib/query-client';

import { MEDIA_PLAN_VERSIONS } from '../constants/EndpointConstants.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  UpdateVersionPayload,
  VersionListForTableResponse,
  VersionListResponse,
  VersionLiteSchema,
  VersionResponse,
} from '../types/buyingPlanVersions.js';
import { useSelectedBuyingPlanId } from './useSelectedBuyingPlanId.js';

const createVersion = async ({
  activeVersionId,
}: {
  activeVersionId: number;
}) => {
  const response = await api.put(
    `${MEDIA_PLAN_VERSIONS}${activeVersionId}/duplicate/`,
  );
  return VersionLiteSchema.parse(response.data);
};

const updateVersion = async ({ versionId, ...rest }: UpdateVersionPayload) => {
  await api.put(`${MEDIA_PLAN_VERSIONS}${versionId}/`, rest);
  return { versionId, ...rest };
};

const deleteVersion = async ({ versionId }: { versionId: number }) => {
  await api.delete(`${MEDIA_PLAN_VERSIONS}${versionId}/`);
  return { versionId };
};

export const useCreateVersionMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VERSION_LIST] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_VERSION_LIST_FOR_TABLE_VIEW],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VERSION] });
    },
  });

  return mutation.mutateAsync;
};

export const useUpdateVersionMutation = () => {
  const queryClient = useQueryClient();
  const selectedPlanId = useSelectedBuyingPlanId();

  return useMutation({
    mutationFn: updateVersion,
    onSuccess: ({ versionId, ...restData }: UpdateVersionPayload) => {
      queryClient.setQueryData<VersionListResponse>(
        [QueryKey.GET_VERSION_LIST, { mediaPlanId: selectedPlanId }],
        (oldData) =>
          oldData
            ? {
                ...oldData,
                versions: oldData.versions.map((v) =>
                  v.id === versionId ? { ...v, ...restData } : v,
                ),
              }
            : oldData,
      );
      queryClient.setQueryData<VersionListForTableResponse>(
        [QueryKey.GET_VERSION_LIST_FOR_TABLE_VIEW, { selectedPlanId }],
        (oldData) =>
          oldData
            ? {
                ...oldData,
                versions: oldData.versions.map((v) =>
                  v.id === versionId ? { ...v, ...restData } : v,
                ),
              }
            : oldData,
      );
      queryClient.setQueryData<VersionResponse>(
        [QueryKey.GET_VERSION, { versionId }],
        (oldData) => (oldData ? { ...oldData, ...restData } : oldData),
      );
    },
  });
};

export const useDeleteVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VERSION_LIST] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_VERSION_LIST_FOR_TABLE_VIEW],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VERSION] });
    },
  });
};
